import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, ArrowUpRight, Loader2 } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { SEO } from "../components/SEO";
import { COMPANY, SERVICES, IMAGES } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-[14px] border border-border bg-white px-5 py-4 text-charcoal placeholder:text-charcoal/40 outline-none transition-colors focus:border-forest";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", location: "", service: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Thank you! Your enquiry has been sent. We'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", location: "", service: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <SEO
        title="Contact & Request a Quote | Kotam Projects Melbourne & Adelaide"
        description="Contact Kotam Projects for a construction quote. Offices in Craigieburn VIC and Pooraka SA. Call 0478 900 097 or send an enquiry — serving Victoria and South Australia."
        path="/contact"
      />
      <section className="relative pt-36 pb-20 sm:pt-44 sm:pb-28 overflow-hidden bg-forest-dark text-white">
        <div className="absolute inset-0 opacity-25">
          <img src={IMAGES.interior2} alt="Luxury home interior finished by Kotam Projects" className="h-full w-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-forest-dark via-forest-dark/80 to-forest-dark/50" />
        <div className="container-px relative">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="label text-gold">
            Contact & Request a Quote
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }} className="mt-6 font-display font-light text-hero max-w-4xl">
            Let's start building.
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }} className="mt-8 max-w-2xl text-lg sm:text-xl text-white/70 leading-relaxed">
            Tell us about your project and we'll arrange a consultation and a transparent quotation.
          </motion.p>
        </div>
      </section>

      <section className="container-px py-24 sm:py-32 lg:py-40">
        <div className="grid gap-14 lg:grid-cols-12 lg:gap-16">
          {/* Form */}
          <div className="lg:col-span-7">
            <Reveal>
              <div className="rounded-[24px] border border-border bg-white p-8 sm:p-12 shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
                <h2 className="font-display text-h3 font-light text-charcoal">Send an enquiry</h2>
                <form onSubmit={submit} className="mt-8 space-y-5" data-testid="enquiry-form">
                  <div className="grid gap-5 sm:grid-cols-2">
                    <div>
                      <label className="label text-charcoal/50">Name *</label>
                      <input data-testid="enquiry-name" className={`mt-2 ${inputCls}`} value={form.name} onChange={update("name")} placeholder="Your full name" />
                    </div>
                    <div>
                      <label className="label text-charcoal/50">Email *</label>
                      <input data-testid="enquiry-email" type="email" className={`mt-2 ${inputCls}`} value={form.email} onChange={update("email")} placeholder="you@email.com" />
                    </div>
                    <div>
                      <label className="label text-charcoal/50">Phone</label>
                      <input data-testid="enquiry-phone" className={`mt-2 ${inputCls}`} value={form.phone} onChange={update("phone")} placeholder="04XX XXX XXX" />
                    </div>
                    <div>
                      <label className="label text-charcoal/50">Location</label>
                      <input data-testid="enquiry-location" className={`mt-2 ${inputCls}`} value={form.location} onChange={update("location")} placeholder="Suburb / City" />
                    </div>
                  </div>
                  <div>
                    <label className="label text-charcoal/50">Service of interest</label>
                    <select data-testid="enquiry-service" className={`mt-2 ${inputCls}`} value={form.service} onChange={update("service")}>
                      <option value="">Select a service</option>
                      {SERVICES.map((s) => (
                        <option key={s.slug} value={s.title}>{s.title}</option>
                      ))}
                      <option value="Other">Other / General enquiry</option>
                    </select>
                  </div>
                  <div>
                    <label className="label text-charcoal/50">Message *</label>
                    <textarea data-testid="enquiry-message" rows={5} className={`mt-2 ${inputCls} resize-none`} value={form.message} onChange={update("message")} placeholder="Tell us about your project, timeline, and budget..." />
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="enquiry-submit"
                    className="group inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-full bg-forest px-8 py-4 font-semibold text-white transition-colors hover:bg-gold hover:text-forest-dark disabled:opacity-60"
                  >
                    {submitting ? (
                      <><Loader2 className="h-5 w-5 animate-spin" /> Sending...</>
                    ) : (
                      <>Request a Quote <ArrowUpRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" /></>
                    )}
                  </button>
                </form>
              </div>
            </Reveal>
          </div>

          {/* Details */}
          <div className="lg:col-span-5 space-y-4">
            <Reveal className="rounded-[24px] bg-forest text-white p-8">
              <p className="label text-gold">Get in touch</p>
              <div className="mt-6 space-y-5">
                <a href={`tel:${COMPANY.phoneRaw}`} className="flex items-center gap-4 group" data-testid="contact-phone">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-white/10"><Phone className="h-5 w-5 text-gold" /></span>
                  <span className="group-hover:text-gold transition-colors">{COMPANY.phone}</span>
                </a>
                <a href={`mailto:${COMPANY.email}`} className="flex items-center gap-4 group" data-testid="contact-email">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-white/10"><Mail className="h-5 w-5 text-gold" /></span>
                  <span className="group-hover:text-gold transition-colors break-all">{COMPANY.email}</span>
                </a>
              </div>
            </Reveal>

            {COMPANY.offices.map((o) => (
              <Reveal key={o.state} delay={0.05} className="rounded-[24px] border border-border bg-white p-8">
                <div className="flex items-start gap-4">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-concrete"><MapPin className="h-5 w-5 text-forest" /></span>
                  <div>
                    <p className="label text-gold">{o.label}</p>
                    <p className="mt-2 text-charcoal/70">{o.address}</p>
                  </div>
                </div>
              </Reveal>
            ))}

            <Reveal delay={0.05} className="rounded-[24px] border border-border bg-white p-8">
              <div className="flex items-center gap-3">
                <Clock className="h-5 w-5 text-forest" />
                <p className="label text-gold">Business Hours</p>
              </div>
              <ul className="mt-5 space-y-3">
                {COMPANY.hours.map((h) => (
                  <li key={h.day} className="flex items-center justify-between border-b border-border/70 pb-3 text-charcoal/70">
                    <span>{h.day}</span><span className="font-medium text-charcoal">{h.time}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Maps */}
      <section className="pb-24 sm:pb-32">
        <div className="container-px">
          <Reveal>
            <p className="label text-gold">Find Us</p>
            <h2 className="mt-4 font-display font-light text-h3 text-charcoal">Serving Victoria & South Australia</h2>
          </Reveal>
          <div className="mt-8 grid gap-6 lg:grid-cols-2">
            <Reveal className="overflow-hidden rounded-[24px] border border-border">
              <iframe
                title="Kotam Projects Victoria Office"
                data-testid="map-victoria"
                src="https://maps.google.com/maps?q=5%2F10%20Nova%20Court%2C%20Craigieburn%20VIC%203064&t=&z=14&ie=UTF8&iwloc=&output=embed"
                className="h-80 w-full"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </Reveal>
            <Reveal delay={0.1} className="overflow-hidden rounded-[24px] border border-border">
              <iframe
                title="Kotam Projects South Australia Office"
                data-testid="map-sa"
                src="https://maps.google.com/maps?q=839-841%20Main%20North%20Rd%2C%20Pooraka%20SA%205095&t=&z=14&ie=UTF8&iwloc=&output=embed"
                className="h-80 w-full"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
